import Box from '@mui/material/Box'
import type { SxProps, Theme } from '@mui/material/styles'
import type { KeyboardEvent } from 'react'

import { transitions, typography } from './KestrixDesignSystem.jsx'
import { KestrixCloseIcon } from './KestrixIcon.jsx'
import { colorTokens } from './tokens/colors.ts'
import { getAccessibleLabelColor } from './tokens/contrast.ts'
import KestrixV2Typography from './typography/KestrixTypography.tsx'

const PILL_BG = colorTokens.global.primary[500]
const NEUTRAL_BG = colorTokens.global.neutral[500]
const NEUTRAL_BORDER = colorTokens.global.neutral[600]
const SUBTEXT_COLOR = colorTokens.text.subtextNeutral

type PillSizeConfig = {
  padding: string
  paddingDismissible: string
  fontSize: string
  fontWeight: number
  lineHeight: string
  fixedHeight: string | undefined
}

const SIZE_CONFIG = {
  sm: {
    padding: '2px 8px',
    paddingDismissible: '2px 6px 2px 8px',
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.normal,
    lineHeight: '18px',
    fixedHeight: '22px'
  },
  md: {
    padding: '2px 10px',
    paddingDismissible: '2px 8px 2px 10px',
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    lineHeight: '20px',
    fixedHeight: undefined
  },
  lg: {
    padding: '4px 12px',
    paddingDismissible: '4px 10px 4px 12px',
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    lineHeight: '20px',
    fixedHeight: undefined
  }
} as const satisfies Record<string, PillSizeConfig>

type PillSize = keyof typeof SIZE_CONFIG

type KestrixPillProps = {
  label: string
  labelColor?: string
  size?: PillSize
  onDismiss?: () => void
  sx?: SxProps<Theme>
  selected?: boolean
  onClick?: () => void
  selectedColor?: string
  selectedBorder?: string
  dataTestId?: string
}

const KestrixPill = ({
  label,
  labelColor,
  size = 'sm',
  onDismiss,
  sx,
  selected = false,
  onClick,
  selectedColor,
  selectedBorder,
  dataTestId
}: KestrixPillProps) => {
  const config = SIZE_CONFIG[size]
  const isDismissible = Boolean(onDismiss)
  const isSelectable = Boolean(onClick)
  const isStaticColor = !isSelectable && selectedColor !== undefined

  const handleKeyDownSelectable = (event: KeyboardEvent<HTMLElement>): void => {
    if (!onClick) {
      return
    }
    if (event.key !== 'Enter' && event.key !== ' ') {
      return
    }
    event.preventDefault()
    onClick()
  }

  const selectedBg = selectedColor ?? PILL_BG
  const selectedOutline = selectedBorder ?? colorTokens.semantic.brand.primary
  const outlinedDismissible = isDismissible && !isSelectable && !isStaticColor

  const baseSx = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: isDismissible ? '4px' : 0,
    borderRadius: '16px',
    padding: isDismissible ? config.paddingDismissible : config.padding,
    height: config.fixedHeight,
    transition: transitions.base
  } as const

  const outerSx: SxProps<Theme> = isSelectable
    ? {
        ...baseSx,
        cursor: 'pointer',
        border: `1px solid ${selected ? selectedOutline : NEUTRAL_BORDER}`,
        backgroundColor: selected ? selectedBg : NEUTRAL_BG,
        ...sx
      }
    : {
        ...baseSx,
        backgroundColor: isStaticColor
          ? selectedBg
          : outlinedDismissible
            ? colorTokens.global.primary[50]
            : PILL_BG,
        border: isStaticColor
          ? `1px solid ${selectedOutline}`
          : outlinedDismissible
            ? `1px solid ${colorTokens.semantic.brand.primary}`
            : 'none',
        '&:hover': outlinedDismissible
          ? { borderColor: colorTokens.semantic.brand.primary }
          : {},
        ...sx
      }

  const effectiveBg = isSelectable
    ? selected
      ? selectedBg
      : NEUTRAL_BG
    : isStaticColor
      ? selectedBg
      : outlinedDismissible
        ? colorTokens.global.primary[50]
        : PILL_BG

  const preferredLabelColor = isSelectable
    ? selected
      ? colorTokens.semantic.brand.base.white
      : SUBTEXT_COLOR
    : isStaticColor
      ? colorTokens.semantic.brand.base.white
      : colorTokens.semantic.brand.primary

  const accessibleLabelColor = getAccessibleLabelColor(
    effectiveBg,
    preferredLabelColor
  )

  return (
    <Box
      onClick={onClick}
      onKeyDown={isSelectable ? handleKeyDownSelectable : undefined}
      role={isSelectable ? 'button' : undefined}
      tabIndex={isSelectable ? 0 : undefined}
      aria-pressed={isSelectable ? selected : undefined}
      data-testid={dataTestId}
      sx={outerSx}
    >
      <KestrixV2Typography
        variant="bodySmall"
        weight="medium"
        align="center"
        noWrap
        sx={{
          color: labelColor || accessibleLabelColor
        }}
      >
        {label}
      </KestrixV2Typography>

      {isDismissible && (
        <Box
          component="button"
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onDismiss?.()
          }}
          aria-label={`Remove ${label}`}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            '&:hover': { opacity: 0.7 }
          }}
        >
          <KestrixCloseIcon
            size={12}
            color={labelColor || accessibleLabelColor}
          />
        </Box>
      )}
    </Box>
  )
}

export { KestrixPill }
